import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  BriefcaseIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface JobPosting {
  id: string;
  title: string;
  description: string;
  location: string;
  remoteType: 'onsite' | 'remote' | 'hybrid';
  jobType: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel: 'entry' | 'mid' | 'senior' | 'executive';
  salaryRange: {
    min: number;
    max: number;
    currency: string;
  };
  skillsRequired: string[];
  requirements: string[];
  benefits: string[];
  status: 'active' | 'paused' | 'closed' | 'draft';
  createdAt: string | { _seconds: number; _nanoseconds: number };
  updatedAt: string | { _seconds: number; _nanoseconds: number };
  applicationDeadline?: string;
  applicationsCount: number;
  viewsCount: number;
  employerId: string;
  employerName: string;
  isActive: boolean;
  isFeatured: boolean;
}

// Helper function to format Firebase timestamps
const formatFirebaseDate = (timestamp: string | { _seconds: number; _nanoseconds: number }) => {
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toLocaleDateString();
  } else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
    return new Date(timestamp._seconds * 1000).toLocaleDateString();
  }
  return 'Invalid Date';
};

const JobPostingsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'closed' | 'draft'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'applications' | 'views'>('newest');
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<JobPosting>>({});

  useEffect(() => {
    fetchJobPostings();
  }, [filter, sortBy]);

  // Open edit modal with job data
  const handleEditJob = (job: JobPosting) => {
    setEditingJob(job);
    setEditFormData({
      title: job.title,
      description: job.description,
      location: job.location,
      remoteType: job.remoteType,
      jobType: job.jobType,
      experienceLevel: job.experienceLevel,
      salaryRange: job.salaryRange,
      status: job.status
    });
  };

  // Save edited job
  const handleSaveEdit = async () => {
    if (!editingJob) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${editingJob.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        // Update the local jobs state
        setJobs(prev => prev.map(job => 
          job.id === editingJob.id ? { ...job, ...editFormData } : job
        ));
        setEditingJob(null);
        setEditFormData({});
        toast.success('Job updated successfully');
      } else {
        throw new Error('Failed to update job');
      }
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job');
    }
  };

  const fetchJobPostings = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      params.append('sortBy', sortBy);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/employer/my?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setJobs(result.data || []);
        } else {
          console.error('Failed to fetch job postings:', result.message);
          setJobs([]);
        }
      } else {
        console.error('Failed to fetch job postings:', response.status, response.statusText);
        setJobs([]);
        return;
      }
    } catch (error: any) {
      console.error('Error fetching job postings:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // ALL MOCK DATA REMOVED - NOW USES REAL API ONLY

  const handleDeleteJob = async (jobId: string) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${jobId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        if (response.ok) {
          setJobs(prev => prev.filter(job => job.id !== jobId));
          toast.success('Job posting deleted successfully');
        } else {
          throw new Error('Failed to delete job posting');
        }
      } catch (error) {
        console.error('Error deleting job:', error);
        toast.error('Failed to delete job posting');
      }
    }
  };

  const handleToggleJobStatus = async (jobId: string, newStatus: 'active' | 'paused') => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${jobId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setJobs(prev => prev.map(job => 
          job.id === jobId ? { ...job, status: newStatus } : job
        ));
        toast.success(`Job ${newStatus === 'active' ? 'activated' : 'paused'} successfully`);
      } else {
        throw new Error('Failed to update job status');
      }
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('Failed to update job status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'paused':
        return <ClockIcon className="h-4 w-4" />;
      case 'closed':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const formatSalary = (salaryRange: JobPosting['salaryRange']) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: salaryRange.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return `${formatter.format(salaryRange.min)} - ${formatter.format(salaryRange.max)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BriefcaseIcon className="h-8 w-8 text-indigo-600" />
                My Postings
              </h1>
              <p className="mt-2 text-gray-600">
                Manage your active job postings and track applications
              </p>
            </div>
            <Link
              to="/employer/job-posting"
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Post New Job
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
                <label className="text-sm font-medium text-gray-700 mr-2">Filter:</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value="all">All Jobs</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="closed">Closed</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-700 mr-2">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="applications">Most Applications</option>
                  <option value="views">Most Views</option>
                </select>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Job Listings */}
        {jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No job postings found</h3>
            <p className="text-gray-600 mb-6">
              {filter === 'all' 
                ? "You haven't created any job postings yet."
                : `No ${filter} job postings found.`
              }
            </p>
            <Link
              to="/employer/job-posting"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Your First Job Posting
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                        {job.isFeatured && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {getStatusIcon(job.status)}
                          <span className="ml-1 capitalize">{job.status}</span>
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {job.location} • {job.remoteType}
                        </div>
                        <div className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          {formatSalary(job.salaryRange)}
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Posted {formatFirebaseDate(job.createdAt)}
                        </div>
                      </div>

                      <p className="text-gray-600 mb-4 line-clamp-2">{job.description}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {job.skillsRequired.slice(0, 4).map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {skill}
                          </span>
                        ))}
                        {job.skillsRequired.length > 4 && (
                          <span className="text-xs text-gray-500">
                            +{job.skillsRequired.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ml-6 flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleJobStatus(
                            job.id, 
                            job.status === 'active' ? 'paused' : 'active'
                          )}
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            job.status === 'active'
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          disabled={job.status === 'draft' || job.status === 'closed'}
                        >
                          {job.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                        
                        <button
                          onClick={() => handleEditJob(job)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Application Statistics */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                      <div>
                        <div className="flex items-center justify-center">
                          <EyeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">{job.viewsCount || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500">Views</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center">
                          <UserGroupIcon className="h-4 w-4 text-blue-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">{job.applicationsCount || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500">Applications</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center">
                          <ClockIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">0</span>
                        </div>
                        <p className="text-xs text-gray-500">Pending</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center">
                          <ChartBarIcon className="h-4 w-4 text-purple-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">0</span>
                        </div>
                        <p className="text-xs text-gray-500">Interviews</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center">
                          <CheckCircleIcon className="h-4 w-4 text-green-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">0</span>
                        </div>
                        <p className="text-xs text-gray-500">Offers</p>
                      </div>
                      <div>
                        <Link
                          to={`/employer/applications?job=${job.id}`}
                          className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium hover:bg-indigo-200"
                        >
                          View Applications
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Edit Job Posting</h2>
                <button
                  onClick={() => setEditingJob(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={editFormData.title || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={editFormData.location || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Remote Type</label>
                    <select
                      value={editFormData.remoteType || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, remoteType: e.target.value as 'onsite' | 'remote' | 'hybrid' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="onsite">On-site</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
                    <select
                      value={editFormData.jobType || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, jobType: e.target.value as 'full-time' | 'part-time' | 'contract' | 'internship' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                    <select
                      value={editFormData.experienceLevel || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, experienceLevel: e.target.value as 'entry' | 'mid' | 'senior' | 'executive' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editFormData.status || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'paused' | 'closed' | 'draft' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setEditingJob(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPostingsPage;